import type { ChatListDataInterface } from '../../components/chat/ChatListItemsMobile';
import type { ChatRoomItemsInterface } from '../../components/chat/ChatRoom';
import type { PinnedMessageItem } from '../../components/chat/ChatListMenu';
import { baseApi } from './baseApi';

type ChatId = number | string;

interface ApiEnvelope<T> {
	data: T;
}

export interface OffsetPageArgs {
	offset: number;
	limit: number;
	sort?: 'asc' | 'desc';
}

export interface ChatMessagesPageArgs extends OffsetPageArgs {
	chatId: ChatId;
	pinnedMessageId?: number;
}

export interface MarkMessagesSeenArgs {
	chatId: ChatId;
	messageIds: number[];
}

export interface CreatePrivateChatArgs {
	recipientUserId: number;
}

export interface CreateGroupChatArgs {
	groupChatName?: string;
	groupChatDescription?: string;
	recipientUserIds: number[];
}

export interface ChatInvitationArgs {
	chatId: ChatId;
	participantId?: ChatId;
}

export interface ChatMessageActionArgs {
	chatId: ChatId;
	messageId: ChatId;
}

export interface UnpinMessageArgs {
	chatId: ChatId;
	pinnedMessagesId: ChatId | { id: ChatId };
}

export interface LeaveChatArgs {
	chatId: ChatId;
	participantId?: ChatId;
}

export interface AttachmentUploadRequest {
	fileName: string;
	mimeType: string;
	size: number;
}

export interface PresignedAttachmentUpload {
	key: string;
	uploadUrl: string;
	fields: Record<string, string>;
	fileName: string;
	mimeType: string;
}

export interface RenewedAttachment {
	key: string;
}

export interface PinnedMessageResponse {
	pinMessages: PinnedMessageItem;
}

export interface DeleteMessagesResponse {
	status: boolean;
}

export interface UnpinnedMessageResponse {
	chatMessageId: number;
}

export interface DeleteAllMessagesResponse {
	pinnedMessagesDeleted: PinnedMessageItem[];
}

const chatListTags = (chats: ChatListDataInterface[] | undefined) => [
	...(chats?.map((chat) => ({ type: 'Chat' as const, id: chat.id })) ?? []),
	{ type: 'Chat' as const, id: 'LIST' },
];

export const chatApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getChatList: build.query<ChatListDataInterface[], OffsetPageArgs>({
			query: ({ offset, limit }) => `/api/chat/list?offset=${offset}&limit=${limit}`,
			transformResponse: (response: ApiEnvelope<ChatListDataInterface[]>) => response.data ?? [],
			providesTags: chatListTags,
		}),
		getContacts: build.query<ChatListDataInterface[], OffsetPageArgs>({
			query: ({ offset, limit, sort = 'asc' }) => `/api/chat/contacts/list?offset=${offset}&limit=${limit}&sort=${sort}`,
			transformResponse: (response: ApiEnvelope<ChatListDataInterface[]>) => response.data ?? [],
			providesTags: chatListTags,
		}),
		getPendingChats: build.query<ChatListDataInterface[], void>({
			query: () => '/api/chat/pending/list',
			transformResponse: (response: ApiEnvelope<ChatListDataInterface[]>) => response.data ?? [],
			providesTags: (chats) => [
				...(chats?.map((chat) => ({ type: 'Chat' as const, id: chat.id })) ?? []),
				{ type: 'Chat' as const, id: 'PENDING' },
			],
		}),
		getPinnedMessages: build.query<PinnedMessageItem[], void>({
			query: () => '/api/chat/message/pinned-messages',
			transformResponse: (response: ApiEnvelope<PinnedMessageItem[]>) => response.data ?? [],
			providesTags: (messages) => [
				...(messages?.map((message) => ({ type: 'PinnedMessage' as const, id: message.id })) ?? []),
				{ type: 'PinnedMessage' as const, id: 'LIST' },
			],
		}),
		getChatDetails: build.query<ChatListDataInterface, ChatId>({
			query: (chatId) => `/api/chat/details/${chatId}`,
			transformResponse: (response: ApiEnvelope<ChatListDataInterface>) => response.data,
			providesTags: (_chat, _error, chatId) => [{ type: 'Chat', id: chatId }],
		}),
		getChatMessages: build.query<ChatRoomItemsInterface[], ChatMessagesPageArgs>({
			query: ({ chatId, offset, limit, pinnedMessageId }) => ({
				url: `/api/chat/message/list?offset=${offset}&limit=${limit}`,
				method: 'POST',
				body: {
					chatId,
					...(pinnedMessageId ? { pinnedMessageInfocus: pinnedMessageId } : {}),
				},
			}),
			transformResponse: (response: ApiEnvelope<ChatRoomItemsInterface[]>) => response.data ?? [],
			providesTags: (messages, _error, args) => [
				...(messages?.map((message) => ({ type: 'ChatMessage' as const, id: message.id })) ?? []),
				{ type: 'ChatRoom' as const, id: args.chatId },
			],
		}),
		markMessagesSeen: build.mutation<ChatRoomItemsInterface[], MarkMessagesSeenArgs>({
			query: (body) => ({
				url: '/api/chat/message/seen-messages',
				method: 'POST',
				body,
			}),
			transformResponse: (response: ApiEnvelope<ChatRoomItemsInterface[]>) => response.data ?? [],
			invalidatesTags: (_messages, _error, { chatId }) => [{ type: 'ChatRoom', id: chatId }],
		}),
		createPrivateChat: build.mutation<ChatListDataInterface, CreatePrivateChatArgs>({
			query: (body) => ({ url: '/api/chat/create-private-chat', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<ChatListDataInterface>) => response.data,
			invalidatesTags: [{ type: 'Chat', id: 'LIST' }],
		}),
		createGroupChat: build.mutation<ChatListDataInterface, CreateGroupChatArgs>({
			query: (body) => ({ url: '/api/chat/create-group-chat', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<ChatListDataInterface>) => response.data,
			invalidatesTags: [{ type: 'Chat', id: 'LIST' }],
		}),
		acceptInvitation: build.mutation<void, ChatInvitationArgs>({
			query: (body) => ({ url: '/api/chat/accept-invitation', method: 'POST', body }),
			transformResponse: () => undefined,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'Chat', id: chatId },
				{ type: 'Chat', id: 'LIST' },
				{ type: 'Chat', id: 'PENDING' },
			],
		}),
		declineInvitation: build.mutation<void, ChatInvitationArgs>({
			query: (body) => ({ url: '/api/chat/decline-invitation', method: 'POST', body }),
			transformResponse: () => undefined,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'Chat', id: chatId },
				{ type: 'Chat', id: 'PENDING' },
			],
		}),
		pinMessage: build.mutation<PinnedMessageResponse, ChatMessageActionArgs>({
			query: (body) => ({ url: '/api/chat/message/pin-message', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<PinnedMessageResponse>) => response.data,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'ChatRoom', id: chatId },
				{ type: 'PinnedMessage', id: 'LIST' },
			],
		}),
		unpinMessage: build.mutation<UnpinnedMessageResponse, UnpinMessageArgs>({
			query: (body) => ({ url: '/api/chat/message/unpin-message', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<UnpinnedMessageResponse>) => response.data,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'ChatRoom', id: chatId },
				{ type: 'PinnedMessage', id: 'LIST' },
			],
		}),
		deleteMessage: build.mutation<DeleteMessagesResponse, ChatMessageActionArgs>({
			query: (body) => ({ url: '/api/chat/message/delete-message', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<DeleteMessagesResponse>) => response.data,
			invalidatesTags: (_result, _error, { chatId }) => [{ type: 'ChatRoom', id: chatId }],
		}),
		deleteAllMessages: build.mutation<DeleteAllMessagesResponse, Pick<ChatInvitationArgs, 'chatId'>>({
			query: (body) => ({ url: '/api/chat/message/delete-all-chat-messages', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<DeleteAllMessagesResponse>) => response.data,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'ChatRoom', id: chatId },
				{ type: 'PinnedMessage', id: 'LIST' },
			],
		}),
		leaveChat: build.mutation<void, LeaveChatArgs>({
			query: (body) => ({ url: '/api/chat/message/leave-chat', method: 'POST', body }),
			transformResponse: () => undefined,
			invalidatesTags: (_result, _error, { chatId }) => [
				{ type: 'Chat', id: chatId },
				{ type: 'Chat', id: 'LIST' },
				{ type: 'ChatRoom', id: chatId },
			],
		}),
		createAttachmentUploads: build.mutation<PresignedAttachmentUpload[], { files: AttachmentUploadRequest[] }>({
			query: (body) => ({ url: '/api/chat/message/presign-attachments', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<PresignedAttachmentUpload[]>) => response.data ?? [],
		}),
		renewAttachment: build.mutation<RenewedAttachment, { key: string; fileName: string }>({
			query: (body) => ({ url: '/api/chat/message/renew/attachment', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<RenewedAttachment>) => response.data,
		}),
		initNotification: build.mutation<void, Record<string, unknown>>({
			query: (body) => ({ url: '/api/chat/notification-init', method: 'POST', body }),
			transformResponse: () => undefined,
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetChatDetailsQuery,
	useLazyGetChatDetailsQuery,
	useLazyGetChatListQuery,
	useLazyGetContactsQuery,
	useLazyGetChatMessagesQuery,
	useLazyGetPendingChatsQuery,
	useLazyGetPinnedMessagesQuery,
	useMarkMessagesSeenMutation,
	useCreatePrivateChatMutation,
	useCreateGroupChatMutation,
	useAcceptInvitationMutation,
	useDeclineInvitationMutation,
	usePinMessageMutation,
	useUnpinMessageMutation,
	useDeleteMessageMutation,
	useDeleteAllMessagesMutation,
	useLeaveChatMutation,
	useCreateAttachmentUploadsMutation,
	useRenewAttachmentMutation,
	useInitNotificationMutation,
} = chatApi;
